/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useRef, useContext, useMemo, useEffect } from 'react'
import { Button, Empty, Input, Modal, Spin, message } from 'antd'
import {
  Grid,
  defaultVirtualizedCellRender,
  virtualizedGridRenderer,
  freeEditEngine,
} from '@integec/grid-tools'
import { Headers } from './LMOAdjustmentEvent'
import { Content, FullHeightSizer } from 'core/screen-util'
import { monthsMap, rowHeaderRender } from 'core'
import { ErrorContext, PNodeZoneContext } from 'context'
import { saveLMOAdjustmentEvent } from 'api/pgads'

const LmoAdjModal = ({
  visible,
  data,
  onClose,
  apiTargetInfo,
  setLoading,
  spinning,
}) => {
  const [gridData, setGridData] = useState(data || [])
  const [editInfo, setEditInfo] = useState(
    freeEditEngine.generateInitialEditInfo()
  )
  const { setError } = useContext(ErrorContext)
  const [fuzzyFilter, setFuzzyFilter] = useState(null)
  const gridRef = useRef()
  const { emktIdUnitNameMap } = useContext(PNodeZoneContext)
  useEffect(() => {
    setGridData(data || [])
    setEditInfo(freeEditEngine.generateInitialEditInfo(data || []))
  }, [data])
  // Sync gridData and editInfo when data prop changes
  // Called when grid cell is edited
  // Called when Save button is clicked
  const saveData = () => {
    const saveData = freeEditEngine.applyEdits({
      data: gridData?.filter(d => d.updated),
      editInfo,
    })
    if (saveData.length <= 0) {
      message.error('Please, update data')
      return
    }
    const requiredData = saveData.map(d => ({
      year: d.year,
      month: d.month,
      unitId: d.unitId,
      eventType: d.eventType,
      eventNum: d.eventNum,
      lmoAdjustment: d.lmoAdjustment,
      comments: d.comments,
      modificationInfo: d.modificationInfo,
    }))
    setLoading(true)
    saveLMOAdjustmentEvent(apiTargetInfo)(requiredData).fork(setError, () => {
      setLoading(false)
      message.success('Lmo Adjustment data saved successfully')
      onClose()
      setEditInfo(freeEditEngine.generateInitialEditInfo())
    })
  }
  const headerWithFormatter = useMemo(() => {
    // Start with your base "Included" column
    // const baseIncluded = strCol({
    //   ident: 'included',
    //   display: 'Included',
    //   ...toSelectionColProps(YNBooleanTypes),
    // })

    // Map the rest of the headers with formatters/props, excluding 'modifier' and 'modifiedDate'
    const mapped = Headers.filter(
      h => h.ident !== 'modifier' && h.ident !== 'modifiedDate'
    ).map(h =>
      h.ident === 'unitId'
        ? {
            ...h,
            // dataGetter: generateDataGetter,
            dataFormatter: ({ value }) => emktIdUnitNameMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              emktIdUnitNameMap[rowData.unitId] ||
              emktIdUnitNameMap[rowData.unitid] ||
              rowData.unitId ||
              rowData.unitid,
          }
        : h.ident === 'month'
        ? {
            ...h,
            dataFormatter: ({ value }) => monthsMap[value] || value,
            sortIndexGetter: ({ rowData }) =>
              monthsMap[rowData.month] || rowData.month,
          }
        : h
    )

    // Return the final header list
    return mapped
  }, [emktIdUnitNameMap])

  const cellRender = props => {
    const { reactVirtualizedProps } = props
    const { style } = reactVirtualizedProps
    //   const type = gridToolProps.headers[reactVirtualizedProps.columnIndex].type
    return defaultVirtualizedCellRender({
      ...props,
      style,
    })
  }

  const editRow = editInfo => (data, setEditInfo, setData) => {
    const modData = data.map(d => {
      if (editInfo.updatedMap.get(d)) {
        return {
          ...editInfo.updatedMap.get(d),
          modificationInfo: undefined,
          updated: true,
        }
      } else {
        return d
      }
    })

    let editData = editInfo.updated
    editData = editData.map(d => ({
      ...d,
      modificationInfo: undefined,
    }))
    setEditInfo({ ...editInfo, updated: editData })
    setData(modData)
  }

  const isEditableComp = ({ header }) => {
    const { ident } = header
    if (ident === 'comments' || ident === 'lmoAdjustment') return true
    return false
  }

  const getGridWidth = defaultWidth => {
    const gridWidth = Headers.filter(
      h => h.ident !== 'modifier' && h.ident !== 'modifiedDate'
    ).reduce((a, b) => a + (b.width || 100), 0)
    return gridWidth < defaultWidth ? gridWidth : defaultWidth
  }

  return (
    <Modal
      title="Card 97 Event"
      visible={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button key="save" type="primary" onClick={saveData}>
          Save
        </Button>,
      ]}
      width="80vw"
    >
      <Spin spinning={spinning}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '10px',
            paddingLeft: '10px',
            paddingRight: '10px',
          }}
        >
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '1.2em',
              marginRight: '20px',
              minWidth: '170px',
            }}
          >
            LMO Adjustment
          </span>
          <Input.Search
            placeholder="Filter"
            value={fuzzyFilter}
            style={{
              flex: '0 0 200px',
              marginLeft: 'auto',
            }}
            onChange={e => setFuzzyFilter(e.target.value)}
          />
        </div>
        <FullHeightSizer heightOffset={380}>
          {({ height, width }) =>
            gridData && gridData.length > 0 ? (
              <Content style={{ height: height - 20 }}>
                <Grid
                  ref={gridRef}
                  data={gridData}
                  sortEnabled
                  headers={headerWithFormatter}
                  altBy={u => u.unitId}
                  altBgColor="#ebebeb"
                  render={virtualizedGridRenderer({
                    rowHeaderRender,
                    height: height,
                    width: getGridWidth(width - 120),
                    cellRender: cellRender,
                    selectionMode: 'single',
                    headerRowHeight: 60,
                  })}
                  editInfo={editInfo}
                  onEditInfoChange={editInfo =>
                    editRow(editInfo)(gridData, setEditInfo, setGridData)
                  }
                  sortOption={headerWithFormatter}
                  fuzzyFilter={fuzzyFilter}
                  isEditable={isEditableComp}
                  editMode="cell"
                  // onSelectionChange={rows => setSelectedRows(rows.selectedRows)}
                  selectionType="row"
                  selectionMode="single"
                />
              </Content>
            ) : (
              <Content style={{ height: '200px' }}>
                <Empty
                  imageStyle={{ paddingTop: '30px' }}
                  description={
                    data && data.length <= 0
                      ? 'No Data from Retrieve'
                      : 'Please click on retrieve button to retrieve data'
                  }
                />
              </Content>
            )
          }
        </FullHeightSizer>
      </Spin>
    </Modal>
  )
}

export default LmoAdjModal
